import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import type { IConsumable } from '@experiences/interfaces';
import { UiText } from '@experiences/ui-common';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    Spacing,
    Typography,
} from '@uipath/apollo-core';
import {
    ApSkeleton,
    PortalErrorPage,
} from '@uipath/portal-shell-react';
import React, {
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import useCanAccessUsageInsights from '../../../../common/hooks/useCanAccessUsageInsights';
import { getTenantConsumables } from '../../../../services/licensing/LicenseService';
import {
    getConsumables,
    getFreeConsumablesOrganization,
    licenseManagementAccountFreeConsumablesOrganizationUrl,
} from '../../../../services/licensing/management/AccountService';
import { accountGlobalId } from '../../../../store/selectors';
import { getErrorStatusCode } from '../../freeConsumablesV2/helpers';
import { generateConsumablesFetchKey } from '../../subcomponents/LicensingDataFetchKeyGenerators';
import UserUsageEntryCard from '../../userUsage/components/UserUsageEntryCard';
import { OverviewPanel } from './OverviewPanel';
import { UiConsumableCardSkeletonV2 } from './UiConsumableCardSkeletonV2';
import { UiConsumableCardV2 } from './UiConsumableCardV2';

const CARD_MIN_WIDTH_PX = 360;
const GRID_GAP_PX = 12;
const SKELETON_CARD_COUNT = 6;
const FREE_SKELETON_CARD_COUNT = 2;

const useStyles = makeStyles(theme =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: Spacing.SpacingBase,
            paddingTop: Spacing.SpacingBase,
        },
        sectionHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingXs,
        },
        sectionTitle: {
            fontSize: Typography.fontSizeMBold.fontSize,
            fontWeight: Typography.fontSizeMBold.fontWeight,
            color: theme.palette.semantic.colorForeground,
        },
        sectionCount: {
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        noLicensesAvailable: {
            fontSize: Typography.fontSizeMBold.fontSize,
            color: theme.palette.semantic.colorForegroundDeEmp,
            fontWeight: Typography.fontSizeMBold.fontWeight,
            marginTop: Spacing.SpacingL,
            marginLeft: Spacing.SpacingL,
            marginBottom: '6px',
        },
        userUsageSkeleton: {
            width: '100%',
            height: '52px',
            borderRadius: Border.BorderRadiusM,
        },
        sectionTitleSkeleton: {
            width: '160px',
            height: '18px',
            borderRadius: Border.BorderRadiusM,
        },
        sectionMessage: {
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        cardsGrid: {
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH_PX}px, 1fr))`,
            // dense: the cards after a full-width Overview panel backfill the rest of its row, so
            // the panel lands under the expanded card's row without any JS column counting.
            gridAutoFlow: 'row dense',
            gap: `${GRID_GAP_PX}px`,
        },
        overviewPanelSpan: { gridColumn: '1 / -1' },
    }),
);

const CardsGrid: React.FC<{
    list: IConsumable[];
    tenantId?: string;
    tenantName?: string;
    isFreeConsumable?: boolean;
    expandedCode: string | null;
    onToggleOverview: (code: string) => void;
}> = ({
    list, tenantId, tenantName, isFreeConsumable = false, expandedCode, onToggleOverview,
}) => {
    const classes = useStyles();

    return (
        <div className={classes.cardsGrid}>
            {list.flatMap(consumable => [
                <UiConsumableCardV2
                    key={consumable.code}
                    consumable={consumable}
                    tenantId={tenantId}
                    isFreeConsumable={isFreeConsumable}
                    overviewExpanded={expandedCode === consumable.code}
                    onToggleOverview={() => onToggleOverview(consumable.code)} />,
                ...(expandedCode === consumable.code ? [
                    <div
                        key={`${consumable.code}-overview`}
                        className={classes.overviewPanelSpan}
                        data-cy="consumables-v2-overview-span">
                        <OverviewPanel
                            consumable={consumable}
                            tenantId={tenantId}
                            tenantName={tenantName} />
                    </div>,
                ] : []),
            ])}
        </div>
    );
};

export const ConsumablesV2: React.FC<{ tenantId?: string; tenantName?: string }> = ({
    tenantId, tenantName,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const accountId = useSelector(accountGlobalId);
    const canAccessUsageInsights = useCanAccessUsageInsights();
    const showUserUsageSection = useFeatureFlagValue(Features.EnableUserUsageView.name)
        && tenantId == null
        && canAccessUsageInsights;
    // Same per-unit visibility flags as the legacy panel.
    const enableRobotUnits = useFeatureFlagValue(Features.EnableRobotUnits.name);
    const enableApiCalls = useFeatureFlagValue(Features.EnableApiCallsUsage.name);
    const enableAppUnits = useFeatureFlagValue(Features.EnableAppUnits.name);
    // Free, self-refreshing allowances are an organization-level concept (same gate as legacy).
    const showFreeSection = useFeatureFlagValue(Features.EnableFreeConsumablesView.name) && tenantId == null;
    const consumablesFetchKey = useMemo(() => generateConsumablesFetchKey(tenantId), [ tenantId ]);

    const {
        data: consumables, error,
    } = useSWR(
        {
            url: consumablesFetchKey,
            accountGlobalId: accountId,
            tenantGlobalId: tenantId,
        },
        (params) => tenantId != null ? getTenantConsumables({
            ...params,
            tenantGlobalId: tenantId,
        }) : getConsumables(params),
    );

    const {
        data: freeConsumables, error: freeError,
    } = useSWR(
        showFreeSection ? {
            url: licenseManagementAccountFreeConsumablesOrganizationUrl,
            accountGlobalId: accountId,
        } : null,
        getFreeConsumablesOrganization,
    );

    // Only one card's Overview panel open at a time, across the whole page.
    const [ expandedCode, setExpandedCode ] = useState<string | null>(null);
    const toggleOverview = (code: string) => setExpandedCode(previous => (previous === code ? null : code));

    // Everything the organization pays for, bundled pools included (e.g. Integration Service API
    // calls) — the "Bundled with organization licenses" section below is the free allowances.
    const purchased = useMemo(() => {
        const hiddenCodes = [
            !enableRobotUnits && 'RU',
            !enableApiCalls && 'APICALLS',
            !enableAppUnits && 'APPU',
        ];
        return consumables?.filter(c => !hiddenCodes.includes(c.code));
    }, [ consumables, enableRobotUnits, enableApiCalls, enableAppUnits ]);

    if (error) {
        return (
            <PortalErrorPage
                type={getErrorStatusCode(error)}
                buttonText={translate({ id: 'CLIENT_TRY_AGAIN' })}
                buttonHref={window.location.href} />
        );
    }

    if (!consumables) {
        return (
            <div className={classes.container}>
                {showUserUsageSection && (
                    <ApSkeleton
                        variant="rectangle"
                        className={classes.userUsageSkeleton} />
                )}
                <div className={classes.sectionHeader}>
                    <ApSkeleton
                        variant="rectangle"
                        className={classes.sectionTitleSkeleton} />
                </div>
                <div className={classes.cardsGrid}>
                    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
                        <UiConsumableCardSkeletonV2 key={index} />
                    ))}
                </div>
            </div>
        );
    }

    if ((!purchased || purchased.length === 0) && !showFreeSection) {
        return (
            <UiText className={classes.noLicensesAvailable}>
                {translate({ id: 'CLIENT_NO_LICENSE_AVAILABLE' })}
            </UiText>
        );
    }

    const renderFreeSection = () => {
        if (freeError) {
            return (
                <UiText
                    className={classes.sectionMessage}
                    data-cy="consumables-v2-free-error">
                    {translate({ id: 'CLIENT_GENERIC_ERROR_MESSAGE' })}
                </UiText>
            );
        }
        if (!freeConsumables) {
            return (
                <div className={classes.cardsGrid}>
                    {Array.from({ length: FREE_SKELETON_CARD_COUNT }).map((_, index) => (
                        <UiConsumableCardSkeletonV2 key={index} />
                    ))}
                </div>
            );
        }
        if (freeConsumables.length === 0) {
            return (
                <UiText
                    className={classes.sectionMessage}
                    data-cy="consumables-v2-free-empty">
                    {translate({ id: 'CLIENT_NO_FREE_CONSUMABLES_AVAILABLE' })}
                </UiText>
            );
        }
        return (
            <CardsGrid
                list={freeConsumables}
                tenantId={tenantId}
                tenantName={tenantName}
                isFreeConsumable
                expandedCode={expandedCode}
                onToggleOverview={toggleOverview} />
        );
    };

    return (
        <div className={classes.container}>
            {showUserUsageSection && <UserUsageEntryCard />}

            {purchased && purchased.length > 0 && (
                <>
                    <div className={classes.sectionHeader}>
                        <UiText className={classes.sectionTitle}>
                            {translate({ id: 'CLIENT_CONSUMABLE_V2_PURCHASED_LICENSES' })}
                        </UiText>
                        <UiText className={classes.sectionCount}>
                            {purchased.length}
                        </UiText>
                    </div>
                    <CardsGrid
                        list={purchased}
                        tenantId={tenantId}
                        tenantName={tenantName}
                        expandedCode={expandedCode}
                        onToggleOverview={toggleOverview} />
                </>
            )}

            {showFreeSection && (
                <>
                    <div className={classes.sectionHeader}>
                        <UiText className={classes.sectionTitle}>
                            {translate({ id: 'CLIENT_CONSUMABLE_V2_BUNDLED_LICENSES' })}
                        </UiText>
                        {freeConsumables && freeConsumables.length > 0 && (
                            <UiText className={classes.sectionCount}>
                                {freeConsumables.length}
                            </UiText>
                        )}
                    </div>
                    {renderFreeSection()}
                </>
            )}
        </div>
    );
};
